import React, { useEffect, Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';

// Layout - loaded immediately (critical for above-the-fold)
import Layout from './components/layout/Layout';

// Optimized loading component - minimal for fast FCP
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FAFFEE]" style={{ contain: 'paint layout' }}>
    <div className="flex flex-col items-center gap-4">
      <div className="w-10 h-10 border-3 border-[#D3FF62] border-t-[#004534] rounded-full animate-spin" style={{ willChange: 'transform' }}></div>
    </div>
  </div>
);

// Critical path: HomePage loads immediately for LCP optimization
const HomePage = lazy(() => import(/* webpackChunkName: "home" */ './pages/HomePage'));

// Secondary pages - lazy loaded with prefetch hints
const VenuesPage = lazy(() => import(/* webpackChunkName: "venues" */ './pages/VenuesPage'));
const ActivitiesPage = lazy(() => import(/* webpackChunkName: "activities" */ './pages/ActivitiesPage'));
const CommunitiesPage = lazy(() => import(/* webpackChunkName: "communities" */ './pages/CommunitiesPage'));
const EventsPage = lazy(() => import(/* webpackChunkName: "events" */ './pages/EventsPage'));
const AboutPage = lazy(() => import(/* webpackChunkName: "about" */ './pages/AboutPage'));
const ContactPage = lazy(() => import(/* webpackChunkName: "contact" */ './pages/ContactPage'));
const BookingPage = lazy(() => import(/* webpackChunkName: "booking" */ './pages/BookingPage'));
const VideosPage = lazy(() => import(/* webpackChunkName: "videos" */ './pages/VideosPage'));
const CoachesPage = lazy(() => import(/* webpackChunkName: "coaches" */ './pages/CoachesPage'));
const PricingPage = lazy(() => import(/* webpackChunkName: "pricing" */ './pages/PricingPage'));
const AdminPage = lazy(() => import(/* webpackChunkName: "admin" */ './pages/AdminPage'));
const BirthdayPage = lazy(() => import(/* webpackChunkName: "birthday" */ './pages/BirthdayPage'));
const CorporateEventsPage = lazy(() => import(/* webpackChunkName: "corporate" */ './pages/CorporateEventsPage'));
const OffersPage = lazy(() => import(/* webpackChunkName: "offers" */ './pages/OffersPage'));

// SEO Landing Pages - lazy loaded
const IndoorFootballDubaiPage = lazy(() => import(/* webpackChunkName: "seo-football" */ './pages/seo/IndoorFootballDubaiPage'));
const FiveASideFootballDubaiPage = lazy(() => import(/* webpackChunkName: "seo-5aside" */ './pages/seo/FiveASideFootballDubaiPage'));
const PadelIndoorDubaiPage = lazy(() => import(/* webpackChunkName: "seo-padel" */ './pages/seo/PadelIndoorDubaiPage'));
const SocialPadelDubaiPage = lazy(() => import(/* webpackChunkName: "seo-social-padel" */ './pages/seo/SocialPadelDubaiPage'));
const AmericanoPadelDubaiPage = lazy(() => import(/* webpackChunkName: "seo-americano" */ './pages/seo/AmericanoPadelDubaiPage'));
const CorporateTeamBuildingDubaiPage = lazy(() => import(/* webpackChunkName: "seo-corporate" */ './pages/seo/CorporateTeamBuildingDubaiPage'));
const BirthdayPartiesDubaiPage = lazy(() => import(/* webpackChunkName: "seo-birthday" */ './pages/seo/BirthdayPartiesDubaiPage'));
const StudioRentalDubaiPage = lazy(() => import(/* webpackChunkName: "seo-studio" */ './pages/seo/StudioRentalDubaiPage'));
const ReformerPilatesDubaiPage = lazy(() => import(/* webpackChunkName: "seo-pilates" */ './pages/seo/ReformerPilatesDubaiPage'));
const AboutMVPLabAlQuozPage = lazy(() => import(/* webpackChunkName: "seo-about" */ './pages/seo/AboutMVPLabAlQuozPage'));

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Main Pages */}
              <Route path="/" element={<Layout><HomePage /></Layout>} />
              <Route path="/venues" element={<Layout><VenuesPage /></Layout>} />
              <Route path="/activities" element={<Layout><ActivitiesPage /></Layout>} />
              <Route path="/communities" element={<Layout><CommunitiesPage /></Layout>} />
              <Route path="/events" element={<Layout><EventsPage /></Layout>} />
              <Route path="/about" element={<Layout><AboutPage /></Layout>} />
              <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
              <Route path="/videos" element={<Layout><VideosPage /></Layout>} />
              <Route path="/coaches" element={<Layout><CoachesPage /></Layout>} />
              <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
              <Route path="/book" element={<Layout><BookingPage /></Layout>} />
              <Route path="/admin" element={<Layout><AdminPage /></Layout>} />
              <Route path="/birthday" element={<Layout><BirthdayPage /></Layout>} />
              <Route path="/corporate" element={<Layout><CorporateEventsPage /></Layout>} />
              <Route path="/offers" element={<Layout><OffersPage /></Layout>} />
              
              {/* SEO Landing Pages */}
              <Route path="/indoor-football-dubai" element={<Layout><IndoorFootballDubaiPage /></Layout>} />
              <Route path="/5-a-side-football-dubai" element={<Layout><FiveASideFootballDubaiPage /></Layout>} />
              <Route path="/padel-indoor-dubai" element={<Layout><PadelIndoorDubaiPage /></Layout>} />
              <Route path="/social-padel-dubai" element={<Layout><SocialPadelDubaiPage /></Layout>} />
              <Route path="/americano-padel-dubai" element={<Layout><AmericanoPadelDubaiPage /></Layout>} />
              <Route path="/corporate-team-building-dubai" element={<Layout><CorporateTeamBuildingDubaiPage /></Layout>} />
              <Route path="/birthday-parties-dubai" element={<Layout><BirthdayPartiesDubaiPage /></Layout>} />
              <Route path="/studio-rental-dubai" element={<Layout><StudioRentalDubaiPage /></Layout>} />
              <Route path="/reformer-pilates-dubai" element={<Layout><ReformerPilatesDubaiPage /></Layout>} />
              <Route path="/about-mvp-lab-al-quoz" element={<Layout><AboutMVPLabAlQuozPage /></Layout>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </HelmetProvider>
  );
}

export default App;
